import { toast } from 'sonner';

function fallbackCopyToClipboard(text: string): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '-9999px';

  const selection = document.getSelection();
  const previousRanges =
    selection && selection.rangeCount > 0
      ? Array.from({ length: selection.rangeCount }, (_, index) => selection.getRangeAt(index))
      : [];
  const activeElement =
    document.activeElement instanceof HTMLElement ? document.activeElement : null;

  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, textarea.value.length);

  let copied = false;

  try {
    copied = document.execCommand('copy');
  } catch {
    copied = false;
  } finally {
    textarea.remove();

    if (selection) {
      selection.removeAllRanges();
      previousRanges.forEach((range) => selection.addRange(range));
    }

    activeElement?.focus();
  }

  return copied;
}

export async function copyToClipboard(
  text: string,
  options: {
    successMessage?: string;
    errorMessage?: string;
    showToast?: boolean;
  } = {},
): Promise<boolean> {
  const {
    successMessage = 'Copied to clipboard',
    errorMessage = 'Failed to copy',
    showToast = true,
  } = options;

  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      throw new Error('Clipboard API unavailable');
    }

    if (showToast) {
      toast.success(successMessage);
    }
    return true;
  } catch (error) {
    if (fallbackCopyToClipboard(text)) {
      if (showToast) {
        toast.success(successMessage);
      }
      return true;
    }

    console.error('Failed to copy:', error);
    if (showToast) {
      toast.error(errorMessage);
    }
    return false;
  }
}
